import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "dulceria_cart_v1";
const REWARD_KEY = "dulceria_pending_reward_v1";

export function CartProvider({ children }) {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [pendingReward, setPendingRewardState] = useState(() => {
        try {
            const raw = localStorage.getItem(REWARD_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    });
    const [open, setOpen] = useState(false);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }, [items]);

    useEffect(() => {
        if (pendingReward) {
            localStorage.setItem(REWARD_KEY, JSON.stringify(pendingReward));
        } else {
            localStorage.removeItem(REWARD_KEY);
        }
    }, [pendingReward]);

    const setPendingReward = (reward) => setPendingRewardState(reward);

    const add = (product, qty = 1) => {
        setItems((prev) => {
            const idx = prev.findIndex((i) => i.product_id === product.id);
            if (idx >= 0) {
                const next = [...prev];
                next[idx] = { ...next[idx], quantity: next[idx].quantity + qty };
                return next;
            }
            return [
                ...prev,
                {
                    product_id: product.id,
                    name: product.name,
                    price: product.price,
                    quantity: qty,
                    image: product.image || "",
                },
            ];
        });
        setOpen(true);
    };

    const remove = (productId) =>
        setItems((prev) => prev.filter((i) => i.product_id !== productId));

    const setQty = (productId, qty) =>
        setItems((prev) =>
            prev
                .map((i) => (i.product_id === productId ? { ...i, quantity: Math.max(1, qty) } : i))
                .filter((i) => i.quantity > 0)
        );

    const clear = () => {
        setItems([]);
        setPendingRewardState(null);
    };

    const total = useMemo(
        () => items.reduce((s, i) => s + Number(i.price) * Number(i.quantity), 0),
        [items]
    );
    const count = useMemo(
        () => items.reduce((s, i) => s + Number(i.quantity), 0),
        [items]
    );

    return (
        <CartContext.Provider
            value={{
                items, add, remove, setQty, clear, total, count, open, setOpen,
                pendingReward, setPendingReward,
            }}
        >
            {children}
        </CartContext.Provider>
    );
}

export const useCart = () => useContext(CartContext);
